import {Component, DestroyRef, inject, Inject, Optional} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {AdxBookingSegmentService} from '../../service/adx-booking-segment.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {combineLatest, debounceTime, distinctUntilChanged, startWith, switchMap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DateService} from 'src/app/shared/service/date/date.service';
import {AirportService} from 'src/app/shared/service/airport/airport-service';
import {HttpParams} from '@angular/common/http';

@Component({
    selector: 'app-add-adx-booking-segment-modal',
    templateUrl: './add-adx-booking-segment-modal.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class AddAdxBookingSegmentModalComponent {
  private readonly destroyRef = inject(DestroyRef);
  addAdxBookingSegmentForm: FormGroup = this.fb.group({});
  
  protected get departDateTimeControl() {
    return this.addAdxBookingSegmentForm.controls['departDateTime'];
  }

  protected get departTimeControl() {
    return this.addAdxBookingSegmentForm.controls['departTime'];
  }
  
  protected get arrivalDateTimeControl() {
    return this.addAdxBookingSegmentForm.controls['arrivalDateTime'];
  }

  protected get arrivalTimeControl() {
    return this.addAdxBookingSegmentForm.controls['arrivalTime'];
  }

  protected get departureCityControl() {
    return this.addAdxBookingSegmentForm.controls['departureCity'];
  }

  protected get arrivalCityControl() {
    return this.addAdxBookingSegmentForm.controls['arrivalCity'];
  }

  protected filteredDepartureCityOptions: any[] = [];
  protected filteredArrivalCityOptions: any[] = [];
  protected isDepartureCityReadOnly = false;
  protected isArrivalCityReadOnly = false;

  constructor(
    @Optional() public dialogRef: MatDialogRef<AddAdxBookingSegmentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { adxBookingId: number, dataSourceId: number },
    private fb: FormBuilder,
    private adxBookingSegmentService: AdxBookingSegmentService,
    private dateService: DateService,
    private airportService: AirportService,
    private _snackBar: MatSnackBar) {
  }

  ngOnInit(){
    this.createForm();
    this.subscribeToDateChanges();
    this.subscribeToArrivalDateRequiredRule();
    this.subscribeToArrivalTimeRequiredRule();
    this.setAutoCompletes();
  }

  public closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }

  createForm(): void {
    this.addAdxBookingSegmentForm = this.fb.group({
      airlineCode: [null,[Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
      flightNumber: [null, [Validators.required, Validators.maxLength(4)]],
      departureCity: [null, [Validators.required]],
      departDateTime: [null, Validators.required],
      departTime: [null, Validators.required],
      arrivalCity: [null, [Validators.required]],
      arrivalDateTime: [null],
      arrivalTime: [null],
      fareBasesCode: [null, [Validators.minLength(1), Validators.maxLength(1)]],
      fare: [null, Validators.pattern(/^[0-9]\d*(\.\d{1,2})?$/)],
      classOfService: [null, [Validators.minLength(1), Validators.maxLength(1)]],
      mileage: [null, Validators.pattern(/^[0-9]\d*(\.\d{1,2})?$/)],
      originConnectionIndicator: [null, [Validators.minLength(1), Validators.maxLength(1)]],
    });
  }

  addSegment(){
    if (this.addAdxBookingSegmentForm.invalid) {
      this.addAdxBookingSegmentForm.markAllAsTouched();
      return;
    }
    const formValues = this.addAdxBookingSegmentForm.value;
    const addSegment = {
      ...formValues,
      departDateTime: this.dateService.getCombinedDateTimeAsString(formValues.departDateTime, formValues.departTime),
      arrivalDateTime: this.dateService.getCombinedDateTimeAsString(formValues.arrivalDateTime, formValues.arrivalTime),
      adxBookingId: this.data.adxBookingId,
      dataSourceId: this.data.dataSourceId,
      departureCity: this.departureCityControl.value ? this.departureCityControl.value.split('-')[0].trim() : null,
      arrivalCity: this.arrivalCityControl.value ? this.arrivalCityControl.value.split('-')[0].trim() : null
    }

    this.adxBookingSegmentService.addAdxBookingSegment(addSegment)
      .subscribe({
          next: (data) => {
            this._snackBar.open('Successfully added booking segment information.', 'Dismiss', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['message-position', 'app-notification-success']
            });
            this.dialogRef.close(data);
          },
          error: () => {
            this._snackBar.open('Failed to add booking segment information.', 'Dismiss', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['message-position', 'app-notification-error']
            });
          }
      });
  }

  get disableReason(): string | null {
    if (!this.addAdxBookingSegmentForm.invalid) { return null; }

    const messages: string[] = [];

    if (this.addAdxBookingSegmentForm.get('airlineCode')?.hasError('required')) messages.push('Airline Code is required.');
    if (this.addAdxBookingSegmentForm.get('airlineCode')?.hasError('minlength') || 
        this.addAdxBookingSegmentForm.get('airlineCode')?.hasError('maxlength')) messages.push('Airline Code must be 2 characters long.');
    if (this.addAdxBookingSegmentForm.get('flightNumber')?.hasError('required')) messages.push('Flight Number is required.');
    if (this.addAdxBookingSegmentForm.get('flightNumber')?.hasError('maxlength')) messages.push('Flight Number must be at most 4 characters long.');
    if (this.addAdxBookingSegmentForm.get('departureCity')?.hasError('required')) messages.push('Departure City is required.');
    if (this.addAdxBookingSegmentForm.get('arrivalCity')?.hasError('required')) messages.push('Arrival City is required.');
    if (this.departDateTimeControl.hasError('required')) messages.push('Depart Date is required.');
    if (this.departTimeControl.hasError('required')) messages.push('Depart Time is required.');
    if (this.arrivalDateTimeControl.hasError('requiredWhenTime')) messages.push('Arrival Date is required when Arrival Time is set.');
    if (this.arrivalTimeControl.hasError('requiredWhenDate')) messages.push('Arrival Time is required when Arrival Date is set.');
    if (this.arrivalDateTimeControl.hasError('invalidDate')) messages.push('Arrival date must be after or equal to depart date.');
    if (this.arrivalTimeControl.hasError('invalidDate')) messages.push('Arrival time must be after or equal to depart time.');

    const fieldErrors = Object.keys(this.addAdxBookingSegmentForm.controls)
      .filter(c => this.addAdxBookingSegmentForm.controls[c].invalid)
      .length;

    if (fieldErrors > 0 && messages.length === 0) {
      messages.push('Please correct the highlighted field(s).');
    }

    return messages.length ? messages.join(' ') : null;
  }

  private subscribeToDateChanges() {
    combineLatest({
      departDate: this.departDateTimeControl.valueChanges.pipe(startWith(this.departDateTimeControl.value)),
      arrivalDate: this.arrivalDateTimeControl.valueChanges.pipe(startWith(this.arrivalDateTimeControl.value)),
      departTime: this.departTimeControl.valueChanges.pipe(startWith(this.departTimeControl.value)),
      arrivalTime: this.arrivalTimeControl.valueChanges.pipe(startWith(this.arrivalTimeControl.value))
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({departDate, arrivalDate, departTime, arrivalTime}) => {
        if (departDate && arrivalDate) {
          const departDateTime = departTime ? this.dateService.getCombinedDateTimeAsDate(departDate, departTime) : new Date(departDate);
          const arrivalDateTime = arrivalTime ? this.dateService.getCombinedDateTimeAsDate(arrivalDate, arrivalTime) : new Date(arrivalDate);

          const isInvalid = departDateTime && arrivalDateTime && departDateTime > arrivalDateTime;
          this.arrivalDateTimeControl.setErrors(isInvalid ? { invalidDate: true } : null);
          
          if (departTime && arrivalTime) {
            this.arrivalTimeControl.setErrors(isInvalid ? { invalidDate: true } : null);
          }
        }
    });
  }

  private subscribeToArrivalDateRequiredRule() {
    combineLatest({
      arrivalDate: this.arrivalDateTimeControl.valueChanges.pipe(startWith(this.arrivalDateTimeControl.value)),
      arrivalTime: this.arrivalTimeControl.valueChanges.pipe(startWith(this.arrivalTimeControl.value))
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ arrivalDate, arrivalTime }) => {
        const hasTimeWithoutDate = !!arrivalTime && !arrivalDate;
        const currentErrors = this.arrivalDateTimeControl.errors || {};

        if (hasTimeWithoutDate) {
          this.arrivalDateTimeControl.setErrors({ ...currentErrors, requiredWhenTime: true });
        } else {
          if (currentErrors['requiredWhenTime']) {
            const { requiredWhenTime, ...others } = currentErrors as any;
            const newErrors = Object.keys(others).length ? others : null;
            this.arrivalDateTimeControl.setErrors(newErrors);
          }
        }
      });
  }

  private subscribeToArrivalTimeRequiredRule() {
    combineLatest({
      arrivalDate: this.arrivalDateTimeControl.valueChanges.pipe(startWith(this.arrivalDateTimeControl.value)),
      arrivalTime: this.arrivalTimeControl.valueChanges.pipe(startWith(this.arrivalTimeControl.value))
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ arrivalDate, arrivalTime }) => {
        const hasDateWithoutTime = !!arrivalDate && !arrivalTime;
        const currentErrors = this.arrivalTimeControl.errors || {};

        if (hasDateWithoutTime) {
          this.arrivalTimeControl.setErrors({ ...currentErrors, requiredWhenDate: true });
        } else if (currentErrors['requiredWhenDate']) {
          const { requiredWhenDate, ...others } = currentErrors as any;
          const newErrors = Object.keys(others).length ? others : null;
          this.arrivalTimeControl.setErrors(newErrors);
        }
      });
  }
  
  private setAutoCompletes(): void {
    this.departureCityControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (value && value.length >= 3) {
          return this.airportService.searchPaginatedAirports(new HttpParams({fromObject: { AutoCompleteSearch: value } }))
          }
          return [];
        }))
      .subscribe(response => this.filteredDepartureCityOptions = response.items);

    this.arrivalCityControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (value && value.length >= 3) {
          return this.airportService.searchPaginatedAirports(new HttpParams({fromObject: { AutoCompleteSearch: value } }))
          }
          return [];
        }))
      .subscribe(response => this.filteredArrivalCityOptions = response.items);
  }

  onDepartureCityOptionSelected(event: any): void {
    const selectedItem = event.option.value;
    this.isDepartureCityReadOnly = true;
    this.departureCityControl.setValue(selectedItem);
  }

  onArrivalCityOptionSelected(event: any): void {
    const selectedItem = event.option.value;
    this.isArrivalCityReadOnly = true;
    this.arrivalCityControl.setValue(selectedItem);
  }

  clearDepartureCitySelection(): void {
    this.isDepartureCityReadOnly = false;
    this.departureCityControl.setValue('');
    this.filteredDepartureCityOptions = [];
  }

  clearArrivalCitySelection(): void {
    this.isArrivalCityReadOnly = false;
    this.arrivalCityControl.setValue('');
    this.filteredArrivalCityOptions = [];
  }
}